import { useCallback, useMemo } from "react";

export const formatMessage = (template, vars = {}) =>
  Object.entries(vars).reduce(
    (msg, [key, value]) => msg.replace(new RegExp(`\\{${key}\\}`, "g"), value),
    template || ""
  );

export const clearDependentFields = (fieldName, formFields, data) => {
  const next = { ...data };
  formFields.forEach((field) => {
    if (field.dependsOn?.field === fieldName) {
      next[field.name] = "";
      Object.assign(next, clearDependentFields(field.name, formFields, next));
    }
  });
  return next;
};

export function useJsonForm({ fields, pageConfig, formData, setFormData, context, fieldErrors }) {
  const validation = pageConfig?.validation || {};
  const selectMessages = pageConfig?.selectMessages || {};

  const visibleFields = useMemo(
    () => (fields || []).filter((field) => field.is_show !== false),
    [fields]
  );

  const getSourceItems = useCallback(
    (sourceKey) => {
      if (!sourceKey) return [];
      const items = context[sourceKey];
      return Array.isArray(items) ? items : [];
    },
    [context]
  );

  const getSelectOptions = useCallback(
    (field) => {
      if (field.options?.length) {
        return field.options.map((opt) =>
          typeof opt === "string" ? { value: opt, label: opt } : opt
        );
      }

      const labelKey = field.optionLabel || "name";
      const valueKey = field.optionValue || "name";
      const sourceItems = getSourceItems(field.optionsSource);

      if (!field.dependsOn) {
        return sourceItems.map((item) => ({
          value: item[valueKey],
          label: item[labelKey],
          id: item.id,
        }));
      }

      const { field: parentField, parentSource, filterKey } = field.dependsOn;
      const parentValue = formData[parentField];
      if (!parentValue) return [];

      const parentItems = getSourceItems(parentSource);
      const parentItem = parentItems.find((item) => item.name === parentValue);
      if (!parentItem) return [];

      return sourceItems
        .filter((item) => Number(item[filterKey]) === Number(parentItem.id))
        .map((item) => ({
          value: item[valueKey],
          label: item[labelKey],
          id: item.id,
        }));
    },
    [formData, getSourceItems]
  );

  const getSelectPlaceholder = useCallback(
    (field) => {
      if (field.dependsOn) {
        const parentValue = formData[field.dependsOn.field];
        if (!parentValue && field.dependsOn.waitMessageKey) {
          return selectMessages[field.dependsOn.waitMessageKey] || "";
        }
      }
      if (field.placeholder) return field.placeholder;
      return formatMessage(selectMessages.default || "Select {label}", {
        label: field.label,
      });
    },
    [formData, selectMessages]
  );

  const isSelectDisabled = useCallback(
    (field) => Boolean(field.dependsOn && !formData[field.dependsOn.field]),
    [formData]
  );

  const handleChange = useCallback(
    (name, value) => {
      setFormData((prev) => {
        const next = { ...prev, [name]: value };
        return clearDependentFields(name, visibleFields, next);
      });
    },
    [setFormData, visibleFields]
  );

  const validateForm = useCallback(() => {
    const formErrors = {};

    visibleFields.forEach((field) => {
      const value = formData[field.name];
      const isEmpty =
        value === undefined || value === null || String(value).trim() === "";

      if (field.required && isEmpty) {
        formErrors[field.name] = formatMessage(validation.fieldRequired, {
          label: field.label,
        });
        return;
      }

      if (!isEmpty && field.type === "number") {
        const numVal = Number(value);
        if (!Number.isNaN(numVal)) {
          if (field.min !== undefined && numVal < field.min) {
            formErrors[field.name] = formatMessage(validation.minValue, {
              label: field.label,
              min: field.min,
            });
          } else if (field.max !== undefined && numVal > field.max) {
            formErrors[field.name] = formatMessage(validation.maxValue, {
              label: field.label,
              max: field.max,
            });
          }
        }
      }
    });

    return formErrors;
  }, [formData, validation, visibleFields]);

  const inputClass = (name) => (fieldErrors?.[name] ? "input-error" : "");

  const renderField = useCallback(
    (field) => {
      if (field.type === "file") return null;

      switch (field.type) {
        case "text":
        case "tel":
        case "number":
        case "url":
        case "email":
          return (
            <input
              type={field.type === "url" ? "url" : field.type}
              placeholder={field.placeholder || field.label}
              value={formData[field.name] || ""}
              className={inputClass(field.name)}
              min={field.type === "number" && field.min !== undefined ? field.min : undefined}
              max={field.type === "number" && field.max !== undefined ? field.max : undefined}
              step={field.type === "number" && field.step !== undefined ? field.step : undefined}
              onChange={(e) => handleChange(field.name, e.target.value)}
            />
          );

        case "textarea":
          return (
            <textarea
              placeholder={field.placeholder || field.label}
              value={formData[field.name] || ""}
              className={inputClass(field.name)}
              rows={field.rows || 5}
              onChange={(e) => handleChange(field.name, e.target.value)}
            />
          );

        case "select": {
          const options = getSelectOptions(field);
          return (
            <select
              value={formData[field.name] || ""}
              className={inputClass(field.name)}
              onChange={(e) => handleChange(field.name, e.target.value)}
              disabled={isSelectDisabled(field)}
            >
              <option value="">{getSelectPlaceholder(field)}</option>
              {options.map((opt) => (
                <option key={opt.id ?? opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          );
        }

        default:
          return null;
      }
    },
    [formData, fieldErrors, getSelectOptions, getSelectPlaceholder, handleChange, isSelectDisabled]
  );

  return {
    visibleFields,
    validateForm,
    handleChange,
    renderField,
    inputClass,
  };
}
